// frontend/src/widgets/VendorPricebooksAdminWidget.jsx
// Project: Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author: Castle Traffic Ltd
// Created: 2026-10-28
// Updated: 2026-10-28 10:08 UTC
// Description: Vendor-owned procurement rate administration. Independent of sales pricebooks.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/axiosInstance";

const END = "/api/vendor-pricebooks";
const errorText = (e) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : e.message || "Request failed");
const today = () => new Date().toISOString().slice(0,10);
const emptyBook = {vendor_customer_id:"", code:"",label:"",currency_code:"GBP"};
const emptyRate = {item_code:"",description:"",unit_label:"each",charge_basis:"quantity",unit_cost:"",tax_rate_percent:"",effective_from:today(),service_id:""};

export default function VendorPricebooksAdminWidget() {
  const [vendors,setVendors]=useState([]);
  const [books,setBooks]=useState([]);
  const [selected,setSelected]=useState(null);
  const [rates,setRates]=useState([]);
  const [bookForm,setBookForm]=useState(emptyBook);
  const [rateForm,setRateForm]=useState(emptyRate);
  const [asOf,setAsOf]=useState("");
  const [working,setWorking]=useState(false);
  const [error,setError]=useState("");
  const [notice,setNotice]=useState("");
  const reload=useCallback(async()=>{
    const [v,b]=await Promise.all([api.get(`${END}/vendors`),api.get(END)]);
    setVendors(v.data||[]);setBooks(b.data||[]);
  },[]);
  useEffect(()=>{reload().catch(e=>setError(errorText(e)));},[reload]);
  useEffect(()=>{
    if (!selected) {setRates([]);return;}
    api.get(`${END}/${selected}/items`,{params:asOf?{as_of:asOf}:{}})
      .then(r=>setRates(r.data||[])).catch(e=>setError(errorText(e)));
  },[selected,asOf]);
  const chosen=useMemo(()=>books.find(b=>String(b.id)===String(selected)),[books,selected]);
  const doAction=async(fn,message)=>{
    setWorking(true);setError("");setNotice("");
    try {await fn();setNotice(message);}catch(e){setError(errorText(e));}finally{setWorking(false);}
  };
  const createBook=async(e)=>{
    e.preventDefault();
    await doAction(async()=>{
      const r=await api.post(END,{...bookForm,vendor_customer_id:Number(bookForm.vendor_customer_id)});
      await reload();setSelected(r.data.id);setBookForm(emptyBook);
    },"Vendor pricebook created.");
  };
  const addRate=async(e)=>{
    e.preventDefault();if(!selected)return;
    await doAction(async()=>{
      await api.post(`${END}/${selected}/items`,{
        ...rateForm,unit_cost:Number(rateForm.unit_cost),
        tax_rate_percent:rateForm.tax_rate_percent===""?null:Number(rateForm.tax_rate_percent),
        service_id:rateForm.service_id===""?null:Number(rateForm.service_id)
      });
      const r=await api.get(`${END}/${selected}/items`,{params:asOf?{as_of:asOf}:{}});
      setRates(r.data||[]);setRateForm(emptyRate);
    },"Historical rate version recorded. Previous rates retained.");
  };
  const deactivate=()=>doAction(async()=>{
    await api.patch(`${END}/${selected}`,{active:false});await reload();
  },"Vendor pricebook deactivated.");
  const fieldStyle={width:"100%",padding:8,border:"1px solid #888",borderRadius:5,background:"transparent",color:"inherit"};
  const formGrid={display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(170px,1fr))",gap:10};
  const change=(set,key)=>(e)=>set(prev=>({...prev,[key]:e.target.value}));
  return <div style={{padding:14,maxWidth:"100%",overflowX:"auto"}} onPointerDownCapture={e=>e.stopPropagation()} onMouseDownCapture={e=>e.stopPropagation()}>
    <h3>Vendor Pricebooks</h3>
    <p>Supplier procurement rates only. Sales Pricebooks and quoting are unaffected.</p>
    {error&&<p role="alert" style={{color:"#c33"}}>{error}</p>}
    {notice&&<p role="status">{notice}</p>}
    <form onSubmit={createBook}>
      <h4>New vendor pricebook</h4>
      <div style={formGrid}>
        <label>Vendor company<select required style={fieldStyle} value={bookForm.vendor_customer_id} onChange={change(setBookForm,"vendor_customer_id")}><option value="">Select vendor</option>{vendors.map(v=><option key={v.id} value={v.id}>{v.name}</option>)}</select></label>
        <label>Code<input required style={fieldStyle} value={bookForm.code} onChange={change(setBookForm,"code")}/></label>
        <label>Label<input required style={fieldStyle} value={bookForm.label} onChange={change(setBookForm,"label")}/></label>
        <label>Currency<select style={fieldStyle} value={bookForm.currency_code} onChange={change(setBookForm,"currency_code")}><option>GBP</option><option>EUR</option><option>USD</option></select></label>
      </div><button disabled={working} type="submit">Create vendor pricebook</button>
    </form>
    <hr/>
    <label>Manage pricebook<select style={fieldStyle} value={selected||""} onChange={e=>{setSelected(e.target.value||null);setAsOf("");setNotice("");}}>
      <option value="">Select pricebook</option>{books.map(b=><option key={b.id} value={b.id}>{b.vendor_name} — {b.code} — {b.label}{!b.active?" (inactive)":""}</option>)}
    </select></label>
    {chosen&&<section>
      <p><strong>{chosen.vendor_name}</strong> · {chosen.currency_code} · {chosen.active?"Active":"Inactive"}</p>
      {chosen.active&&<button type="button" disabled={working} onClick={deactivate}>Deactivate pricebook</button>}
      <h4>Rate history</h4>
      <label>View effective rates as of date (blank = full history) <input type="date" style={{...fieldStyle,maxWidth:220}} value={asOf} onChange={e=>setAsOf(e.target.value)}/></label>
      <div style={{overflowX:"auto"}}><table style={{width:"100%",borderCollapse:"collapse"}}><thead><tr>{["Item","Description","Unit","Basis","Cost","Tax %","Effective from"].map(h=><th key={h} style={{textAlign:"left",padding:6}}>{h}</th>)}</tr></thead><tbody>
      {rates.map(r=><tr key={r.id}>{[r.item_code,r.description,r.unit_label,r.charge_basis,`${chosen.currency_code} ${r.unit_cost}`,r.tax_rate_percent??"—",r.effective_from].map((v,i)=><td key={i} style={{padding:6,borderTop:"1px solid #888"}}>{String(v)}</td>)}</tr>)}
      {!rates.length&&<tr><td colSpan={7}>No rates for this selection.</td></tr>}</tbody></table></div>
      {chosen.active&&<form onSubmit={addRate}>
        <h4>Add effective-dated rate version</h4>
        <div style={formGrid}>
          {[["Item code","item_code","text"],["Description","description","text"],["Unit label","unit_label","text"],["Unit cost","unit_cost","number"],["Tax % (optional)","tax_rate_percent","number"],["Effective from","effective_from","date"],["Service ID (optional)","service_id","number"]].map(([label,key,type])=><label key={key}>{label}<input style={fieldStyle} type={type} required={!(["tax_rate_percent","service_id"].includes(key))} min={type==="number"?0:undefined} step={type==="number"?"0.0001":undefined} value={rateForm[key]} onChange={change(setRateForm,key)}/></label>)}
          <label>Charge basis<select style={fieldStyle} value={rateForm.charge_basis} onChange={change(setRateForm,"charge_basis")}>
            {[["fixed","Fixed fee"],["hour","Hourly"],["day","Daily"],["quantity","Quantity"],["other","Other"]].map(([key,label])=><option key={key} value={key}>{label}</option>)}
          </select></label>
        </div><p>Rates are append-only. To change a price, add another row with a later effective date.</p>
        <button disabled={working} type="submit">Add rate version</button>
      </form>}
    </section>}
  </div>;
}
